import { useState } from "react";
import { Button, Flex, Loader, ModalV2, Select, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useGetReviewersQuery } from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import type { AssignReviewerModalProps } from "./types";

export const AssignReviewerModal = ({
  isOpen,
  onClose,
  onSubmit,
}: AssignReviewerModalProps) => {
  const [reviewer, setReviewer] = useState<string>("");

  const { data, loading, error } = useGetReviewersQuery({
    skip: !isOpen,
    fetchPolicy: "cache-and-network",
  });

  const reviewers = data?.getReviewers ?? [];

  const handleReviewerChange = (value: string | number | string[] | number[]) => {
    if (typeof value === "string") {
      setReviewer(value);
    }
  };

  const handleSubmit = () => {
    const parsed = Number(reviewer);
    if (!Number.isNaN(parsed) && parsed > 0) {
      onSubmit(parsed);
    }
    setReviewer("");
    onClose();
  };

  const handleClose = () => {
    setReviewer("");
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ModalV2 show={isOpen} onHide={handleClose} title="Assign reviewer">
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.SMALL}>
          {loading && <Loader loading />}
          {error && (
            <Text variant="regular-md" color={Colors.Danger600}>
              Failed to load reviewers
            </Text>
          )}
          {!loading && !error && (
            <Select
              label="Reviewer"
              placeholder="Select reviewer"
              value={reviewer}
              onChange={handleReviewerChange}
              full
              data-testid="reviewer-select"
            >
              <Select.Section>
                {reviewers.map((r) => (
                  <Select.Option key={r.id} value={String(r.id)}>
                    {r.displayName}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          )}
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
          <Button variant="neutral" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!reviewer || loading}>
            Submit for review
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
